import React from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Badge,
  ProgressBar,
} from 'react-bootstrap';
import {
  FaChartLine,
  FaUsers,
  FaEnvelope,
  FaComments,
  FaArrowUp,
  FaArrowDown,
  FaEquals,
} from 'react-icons/fa';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTheme } from '../../contexts/ThemeContext';


const DiscussionStats = ({ stats }) => {
  const { isDark } = useTheme();

  if (!stats) {
    return (
      <div className="discussion-empty-state">
        <FaChartLine size={48} className="mb-3" />
        <p>Aucune statistique disponible</p>
      </div>
    );
  }


  const {
    total_conversations = 0,
    total_messages = 0,
    unread_messages = 0,
    active_conversations = 0,
    messages_today = 0,
    messages_this_week = 0,
    messages_this_month = 0,
    top_active_users = [],
    conversations_by_day = [],
    message_types = { text: 0, image: 0, video: 0, audio: 0 },
  } = stats;

  const totalMedia = message_types.image + message_types.video + message_types.audio;
  const textPercentage = total_messages > 0 ? Math.round((message_types.text / total_messages) * 100) : 0;
  const mediaPercentage = total_messages > 0 ? Math.round((totalMedia / total_messages) * 100) : 0;

  return (
    <div className={`discussion-stats-container p-4 ${isDark ? 'dark' : ''}`}>

      {/* Summary Cards */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="stat-card h-100 border-primary">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-2">Messages Aujourd'hui</h6>
                  <h4 className="mb-0">{messages_today}</h4>
                </div>
                <div className="bg-primary bg-opacity-10 p-2 rounded">
                  <FaEnvelope className="text-primary" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card h-100 border-info">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-2">Cette Semaine</h6>
                  <h4 className="mb-0">{messages_this_week}</h4>
                </div>
                <div className="bg-info bg-opacity-10 p-2 rounded">
                  <FaChartLine className="text-info" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card h-100 border-success">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-2">Ce Mois</h6>
                  <h4 className="mb-0">{messages_this_month}</h4>
                </div>
                <div className="bg-success bg-opacity-10 p-2 rounded">
                  <FaComments className="text-success" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card h-100 border-warning">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-2">Taux de Réponse</h6>
                  <h4 className="mb-0">
                    {total_messages > 0
                      ? Math.round(((total_messages - unread_messages) / total_messages) * 100)
                      : 0}%
                  </h4>
                </div>
                <div className="bg-warning bg-opacity-10 p-2 rounded">
                  <FaUsers className="text-warning" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

      </Row>

      {/* Message Types Distribution */}
      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Types de Messages</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span><FaEnvelope className="me-2" />Texte</span>
                  <span>{message_types.text} ({textPercentage}%)</span>
                </div>
                <ProgressBar now={textPercentage} variant="primary" />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Images</span>
                  <span>{message_types.image}</span>
                </div>
                <ProgressBar 
                  now={total_messages > 0 ? (message_types.image / total_messages) * 100 : 0} 
                  variant="info" 
                />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>Vidéos</span>
                  <span>{message_types.video}</span>
                </div>
                <ProgressBar 
                  now={total_messages > 0 ? (message_types.video / total_messages) * 100 : 0} 
                  variant="success" 
                />
              </div>
              <div>
                <div className="d-flex justify-content-between mb-1">
                  <span>Audio</span>
                  <span>{message_types.audio}</span>
                </div>
                <ProgressBar 
                  now={total_messages > 0 ? (message_types.audio / total_messages) * 100 : 0} 
                  variant="warning" 
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">Activité par Jour (7 derniers jours)</h6>
            </Card.Header>
            <Card.Body>
              {conversations_by_day.length === 0 ? (
                <p className="text-center py-3" style={{ color: 'var(--chatoo-text-secondary)' }}>Aucune donnée disponible</p>
              ) : (

                <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                  {conversations_by_day.map((day, index) => (
                    <div key={day.date || index} className="mb-3">
                      <div className="d-flex justify-content-between mb-1">
                        <small>{format(new Date(day.date), 'EEEE dd/MM', { locale: fr })}</small>
                        <small>{day.count} messages</small>
                      </div>
                      <ProgressBar 
                        now={Math.max(...conversations_by_day.map(d => d.count)) > 0 
                          ? (day.count / Math.max(...conversations_by_day.map(d => d.count))) * 100 
                          : 0} 
                        variant="primary"
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top Active Users */}
      <Card>
        <Card.Header>
          <h6 className="mb-0">Utilisateurs les Plus Actifs</h6>
        </Card.Header>
        <Card.Body>
          {top_active_users.length === 0 ? (
            <p className="text-center py-3" style={{ color: 'var(--chatoo-text-secondary)' }}>Aucune donnée disponible</p>
          ) : (

            <Table responsive hover>
              <thead>
                <tr>
                  <th>Rang</th>
                  <th>Utilisateur</th>
                  <th>Messages Envoyés</th>
                  <th>Messages Reçus</th>
                  <th>Total</th>
                  <th>Tendance</th>
                </tr>
              </thead>
              <tbody>
                {top_active_users.map((user, index) => (
                  <tr key={user.user_id || index}>

                    <td>
                      <Badge bg={index < 3 ? 'warning' : 'secondary'}>
                        #{index + 1}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {user.profile_picture ? (
                          <img
                            src={user.profile_picture}
                            alt={user.username}
                            className="rounded-circle"
                            style={{ width: 32, height: 32, objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            className="rounded-circle bg-secondary d-flex align-items-center justify-content-center"
                            style={{ width: 32, height: 32 }}
                          >
                            <FaUsers className="text-white" size={14} />
                          </div>
                        )}
                        <span>{user.name || user.username}</span>
                      </div>
                    </td>
                    <td>{user.messages_sent || 0}</td>
                    <td>{user.messages_received || 0}</td>
                    <td>
                      <strong>{(user.messages_sent || 0) + (user.messages_received || 0)}</strong>
                    </td>

                    <td>
                      {user.trend === 'up' && <FaArrowUp className="text-success" />}
                      {user.trend === 'down' && <FaArrowDown className="text-danger" />}
                      {user.trend === 'stable' && <FaEquals className="text-muted" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default DiscussionStats;
